import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, MapPin, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  testimonials as fallbackTestimonials, 
  getTestimonialsByService, 
  trustStats,
  getAverageRating,
  getTotalReviews 
} from '@/data/reviewsConfig';
import api from '@/services/api';

// Star rating component
export const StarRating = ({ rating, size = 'default' }) => {
  const sizeClass = size === 'small' ? 'h-3 w-3' : size === 'large' ? 'h-6 w-6' : 'h-4 w-4';
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

// Single testimonial card
export const TestimonialCard = ({ testimonial, variant = 'default' }) => {
  const { name, location, rating, text, service, verified } = testimonial;
  
  if (variant === 'compact') {
    return (
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center gap-2 mb-2">
          <StarRating rating={rating} size="small" />
          {verified && <CheckCircle className="h-3 w-3 text-green-500" />}
        </div>
        <p className="text-sm text-gray-700 line-clamp-3">{text}</p>
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <span>{name}</span>
          <span className="flex items-center"><MapPin className="h-3 w-3 mr-1" />{location}</span>
        </div>
      </div>
    );
  }
  
  return (
    <Card className="h-full">
      <CardContent className="p-6">
        <Quote className="h-8 w-8 text-[#039fff] opacity-30 mb-4" />
        <div className="flex items-center gap-2 mb-3">
          <StarRating rating={rating} />
          {verified && (
            <Badge variant="outline" className="text-xs text-green-600 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>
        <p className="text-gray-700 mb-4">{text}</p>
        <div className="flex items-center justify-between border-t pt-4">
          <div>
            <p className="font-semibold text-[#001A47]">{name}</p>
            <p className="text-sm text-gray-500 flex items-center">
              <MapPin className="h-3 w-3 mr-1" />
              {location}
            </p>
          </div>
          <Badge variant="secondary" className="capitalize">
            {service === 'mini-move' ? 'Mini-Move' : service}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Testimonials grid - fetches from API
export const TestimonialsGrid = ({ 
  service = 'all', 
  limit = 6, 
  columns = 3,
  showTitle = true 
}) => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const params = new URLSearchParams();
        if (service && service !== 'all') params.append('service', service);
        params.append('limit', limit.toString());
        
        const response = await api.get(`/api/admin/public/reviews?${params}`);
        if (response.data.success) {
          setTestimonials(response.data.reviews);
          setStats(response.data.stats || { total_reviews: 0, average_rating: 0 });
        }
      } catch (error) {

        // Use fallback testimonials
        const filtered = getTestimonialsByService(service).slice(0, limit);
        setTestimonials(filtered);
        setStats({ total_reviews: getTotalReviews(), average_rating: parseFloat(getAverageRating()) });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReviews();
  }, [service, limit]);

  // Don't render if no reviews
  if (!isLoading && testimonials.length === 0) {
    return null;
  }
  
  const columnClass = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4'
  }[columns] || 'md:grid-cols-3';
  
  return (
    <div>
      {showTitle && (
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#001A47] mb-2">What Our Customers Say</h2>
          <div className="flex items-center justify-center gap-2">
            <StarRating rating={5} />
            <span className="text-gray-600">
              {stats.average_rating} average from {stats.total_reviews} reviews
            </span>
          </div>
        </div>
      )}
      <div className={`grid grid-cols-1 ${columnClass} gap-6`}>
        {testimonials.map((testimonial) => (
          <TestimonialCard key={testimonial.review_id || testimonial.id} testimonial={testimonial} />
        ))}
      </div>
    </div>
  );
};

// Testimonials carousel/slider - fetches from API
export const TestimonialsCarousel = ({ service = 'all' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const params = new URLSearchParams();
        if (service && service !== 'all') params.append('service', service);
        params.append('limit', '10');
        
        const response = await api.get(`/api/admin/public/reviews?${params}`);
        if (response.data.success && response.data.reviews.length > 0) {
          setTestimonials(response.data.reviews);
        } else {
          // Use fallback
          setTestimonials(getTestimonialsByService(service));
        }
      } catch (error) {

        setTestimonials(getTestimonialsByService(service));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReviews();
  }, [service]);
  
  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };
  
  const prev = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };
  
  // Don't render if no testimonials
  if (!isLoading && testimonials.length === 0) {
    return null;
  }
  
  if (isLoading || testimonials.length === 0) {
    return null;
  }
  
  const testimonial = testimonials[currentIndex];
  
  return (
    <div className="relative max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-8 text-center">
          <Quote className="h-12 w-12 text-[#039fff] opacity-30 mx-auto mb-6" />
          <div className="flex justify-center mb-4">
            <StarRating rating={testimonial.rating} size="large" />
          </div>
          <p className="text-lg text-gray-700 mb-6 italic">"{testimonial.text}"</p>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#039fff] flex items-center justify-center text-white font-bold">
              {testimonial.name.charAt(0)}
            </div>
            <div className="text-left">
              <p className="font-semibold text-[#001A47]">{testimonial.name}</p>
              <p className="text-sm text-gray-500 flex items-center">
                <MapPin className="h-3 w-3 mr-1" />
                {testimonial.location}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-6">
        <Button variant="outline" size="icon" onClick={prev}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex gap-2">
          {testimonials.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-[#039fff]' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
        <Button variant="outline" size="icon" onClick={next}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Trust stats bar
export const TrustStatsBar = ({ variant = 'default' }) => {
  const stats = [
    { label: 'Deliveries', value: trustStats.totalDeliveries },
    { label: 'Happy Customers', value: trustStats.happyCustomers },
    { label: 'Avg Rating', value: trustStats.avgRating },
    { label: 'On-Time Rate', value: trustStats.onTimeRate }
  ];
  
  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap justify-center gap-6 py-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="text-center">
            <p className="text-2xl font-bold text-[#039fff]">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>
    );
  }
  
  return (
    <div className="bg-[#001A47] py-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-[#039fff]">{stat.value}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Reviews summary component
export const ReviewsSummary = ({ showLink = true }) => {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-1">
        <StarRating rating={5} />
        <span className="font-bold text-[#001A47]">{getAverageRating()}</span>
      </div>
      <span className="text-gray-500">|</span>
      <span className="text-gray-600">{getTotalReviews()} verified reviews</span>
      {showLink && (
        <>
          <span className="text-gray-500">|</span>
          <a href="#testimonials" className="text-[#039fff] hover:underline text-sm">
            Read all reviews
          </a>
        </>
      )}
    </div>
  );
};

export default TestimonialsGrid;
